import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, Rocket, Sparkles, CheckCircle2, AlertTriangle, Save,
  RefreshCw, UserCircle2, FileDown,
} from "lucide-react";
import { careerCockpitApi } from "@/api/careerCockpit";
import { downloadVaultAsset } from "@/api/compliance";
import CreditPaywall from "@/components/billing/CreditPaywall";
import ResumeRefresh from "@/components/dashboard/ResumeRefresh";

/* Phase 28 — Career Cockpit Auto-Pilot tab. Instant context hydration +
   real LLM resume tailoring against a pasted job description. */
export default function CareerAutoPilot() {
  const [mode, setMode] = useState("refresh"); // refresh | tailor
  const [ctx, setCtx] = useState(null);
  const [resume, setResume] = useState("");
  const [jobDesc, setJobDesc] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [packing, setPacking] = useState(false);
  const [paywall, setPaywall] = useState(null);

  const hydrate = async () => {
    try {
      const d = await careerCockpitApi.context();
      setCtx(d);
      if (d.base_resume && !resume) setResume(d.base_resume);
    } catch { setCtx({ error: true }); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { hydrate(); }, []);

  const engage = async () => {
    if (!jobDesc.trim()) { toast.error("Paste the target job description"); return; }
    if (!resume.trim()) { toast.error("Paste or hydrate your resume first"); return; }
    setBusy(true); setResult(null); setSaved(false);
    try {
      const d = await careerCockpitApi.tailor(jobDesc.trim(), resume.trim());
      setResult(d);
      toast.success("Auto-Pilot tailored your resume");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auto-Pilot failed");
    } finally { setBusy(false); }
  };

  const saveBase = async () => {
    if (!resume.trim()) { toast.error("Nothing to save"); return; }
    try {
      await careerCockpitApi.saveBaseResume(resume.trim(), ctx?.target_role || "");
      toast.success("Base resume saved — cockpit will auto-hydrate next time");
      hydrate();
    } catch { toast.error("Save failed"); }
  };

  const saveTailored = async () => {
    if (!result) return;
    const body = [
      result.tailored_summary,
      "",
      ...(result.tailored_bullets || []).map((b) => `• ${b}`),
    ].join("\n");
    try {
      await careerCockpitApi.saveTailored(body, "Tailored Resume");
      setSaved(true);
      toast.success("Saved to Vault");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const buildApplyPack = async () => {
    if (!result) return;
    setPacking(true);
    try {
      const r = await careerCockpitApi.applyPack({
        job_desc: jobDesc.trim(),
        role: ctx?.target_role || "",
        tailored_summary: result.tailored_summary || "",
        tailored_bullets: result.tailored_bullets || [],
      });
      await downloadVaultAsset(r.asset_id, r.filename);
      toast.success(r.charged ? `Apply Pack ready · ${r.cost} Credits spent` : "Apply Pack ready — downloaded");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 402 && detail) setPaywall(detail);
      else toast.error(typeof detail === "string" ? detail : "Apply Pack failed");
    } finally { setPacking(false); }
  };

  const score = result?.match_score ?? 0;

  return (
    <div className="space-y-3" data-testid="career-autopilot">
      {/* Workspace tabs — both career tools share one workspace */}
      <div className="flex gap-2">
        <button onClick={() => setMode("refresh")} data-testid="career-tab-refresh" className={`flex-1 font-mono text-[9px] font-black uppercase tracking-widest py-2.5 rounded-full border-2 border-charcoal ${mode === "refresh" ? "bg-gold text-charcoal" : "bg-white text-charcoal/70"}`}>Refresh Resume</button>
        <button onClick={() => setMode("tailor")} data-testid="career-tab-tailor" className={`flex-1 font-mono text-[9px] font-black uppercase tracking-widest py-2.5 rounded-full border-2 border-charcoal ${mode === "tailor" ? "bg-gold text-charcoal" : "bg-white text-charcoal/70"}`}>Tailor to a Job</button>
      </div>

      {mode === "refresh" ? <ResumeRefresh /> : (
        <>
          {/* Hydration status card */}
      <div className="border-4 border-charcoal rounded-2xl bg-charcoal p-3.5 shadow-[3px_3px_0px_#1A1A1A]" data-testid="autopilot-context">
        <div className="flex items-center gap-2 mb-2">
          <Rocket size={15} className="text-gold" />
          <p className="font-mono text-[9px] uppercase tracking-[0.24em] text-gold">Auto-Pilot Cockpit</p>
        </div>
        {ctx == null ? (
          <div className="flex items-center gap-2 text-white/70"><Loader2 size={13} className="animate-spin" /><span className="font-mono text-[10px]">Hydrating context…</span></div>
        ) : ctx.error ? (
          <p className="font-mono text-[10px] text-rust">Context unavailable.</p>
        ) : (
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-xl bg-white/10 border-2 border-gold grid place-items-center shrink-0"><UserCircle2 size={18} className="text-gold" /></span>
            <div className="min-w-0 flex-1">
              <p className="font-mono text-xs font-black text-white truncate">{ctx.identity?.name}</p>
              <p className="font-mono text-[8px] text-white/70 uppercase tracking-widest">
                {ctx.milestone_count} milestones · {ctx.application_count} applications · {ctx.has_base_resume ? "resume on file" : "no base resume"}
              </p>
            </div>
            <button onClick={hydrate} data-testid="autopilot-rehydrate" className="w-8 h-8 grid place-items-center rounded-lg bg-white/10 border border-gold/50"><RefreshCw size={13} className="text-gold" /></button>
          </div>
        )}
      </div>

      {/* Resume input (auto-hydrated) */}
      <div className="border-4 border-charcoal rounded-2xl bg-white p-3 shadow-[3px_3px_0px_#1A1A1A]" data-testid="autopilot-resume-form">
        <div className="flex items-center justify-between mb-2">
          <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-charcoal/70">Your Resume</p>
          <button onClick={saveBase} data-testid="autopilot-save-base" className="flex items-center gap-1 font-mono text-[8px] font-black uppercase tracking-widest text-charcoal/60"><Save size={11} /> Save Base</button>
        </div>
        <textarea value={resume} rows={5} onChange={(e) => setResume(e.target.value)} data-testid="autopilot-resume-input" placeholder="Paste your resume — or Save Base once and it auto-hydrates here." className="w-full bg-cream border-2 border-charcoal rounded-xl px-3 py-2.5 font-mono text-xs outline-none focus:ring-2 focus:ring-charcoal resize-none" />
      </div>

      {/* Target job description */}
      <div className="border-4 border-charcoal rounded-2xl bg-white p-3 shadow-[3px_3px_0px_#1A1A1A]" data-testid="autopilot-jd-form">
        <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-charcoal/70 mb-2">Target Job Description</p>
        <textarea value={jobDesc} rows={4} onChange={(e) => setJobDesc(e.target.value)} data-testid="autopilot-jd-input" placeholder="Paste the role you're chasing…" className="w-full bg-cream border-2 border-charcoal rounded-xl px-3 py-2.5 font-mono text-xs outline-none focus:ring-2 focus:ring-charcoal resize-none" />
        <button onClick={engage} disabled={busy} data-testid="autopilot-engage-btn" className="mt-2 w-full flex items-center justify-center gap-2 bg-charcoal text-gold font-black font-mono text-[11px] uppercase tracking-widest py-3 rounded-full border-2 border-charcoal shadow-[3px_3px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-60">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Engage Auto-Pilot
        </button>
      </div>

      {/* Tailored result */}
      {result && (
        <div className="border-4 border-charcoal rounded-2xl bg-white p-3.5 shadow-[3px_3px_0px_#1A1A1A] space-y-3" data-testid="autopilot-result">
          <div className="flex items-center gap-3 bg-charcoal rounded-xl p-3">
            <div className="text-center shrink-0">
              <p className="font-mono text-[8px] uppercase tracking-[0.25em] text-gold">Match</p>
              <p className="font-serif text-4xl text-white tabular-nums leading-none">{score}</p>
            </div>
            <p className="font-mono text-[10px] text-white/70 leading-snug">{result.verdict}</p>
          </div>

          {result.tailored_summary && (
            <div data-testid="autopilot-summary">
              <p className="font-mono text-[8px] uppercase tracking-[0.2em] text-charcoal/70 mb-1">Tailored Summary</p>
              <p className="font-mono text-[11px] text-charcoal leading-relaxed">{result.tailored_summary}</p>
            </div>
          )}

          {!!(result.tailored_bullets || []).length && (
            <div data-testid="autopilot-bullets">
              <p className="font-mono text-[8px] uppercase tracking-[0.2em] text-charcoal/70 mb-1">Rewritten Bullets</p>
              <ul className="space-y-1.5">
                {result.tailored_bullets.map((b, i) => (
                  <li key={i} className="flex gap-2 font-mono text-[11px] text-charcoal leading-snug"><span className="text-gold font-black">▸</span>{b}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            {!!(result.strengths || []).length && (
              <div className="bg-emerald-50 border-2 border-emerald-600 rounded-xl p-2" data-testid="autopilot-strengths">
                <p className="flex items-center gap-1 font-mono text-[8px] uppercase tracking-widest text-emerald-700 mb-1"><CheckCircle2 size={10} /> Strengths</p>
                {result.strengths.map((x, i) => <p key={i} className="font-mono text-[9px] text-emerald-800 leading-snug">• {x}</p>)}
              </div>
            )}
            {!!(result.gaps || []).length && (
              <div className="bg-amber-50 border-2 border-amber-600 rounded-xl p-2" data-testid="autopilot-gaps">
                <p className="flex items-center gap-1 font-mono text-[8px] uppercase tracking-widest text-amber-700 mb-1"><AlertTriangle size={10} /> Gaps</p>
                {result.gaps.map((x, i) => <p key={i} className="font-mono text-[9px] text-amber-800 leading-snug">• {x}</p>)}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-2">
            <button onClick={saveTailored} disabled={saved} data-testid="autopilot-save-vault" className="flex items-center justify-center gap-1.5 bg-white text-charcoal font-black font-mono text-[9px] uppercase tracking-widest py-3 rounded-full border-2 border-charcoal shadow-[2px_2px_0px_#1A1A1A] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none disabled:opacity-60">
              {saved ? <><CheckCircle2 size={13} /> Saved</> : <><Save size={13} /> Save to Vault</>}
            </button>
            <button onClick={buildApplyPack} disabled={packing} data-testid="autopilot-apply-pack" className="flex items-center justify-center gap-1.5 bg-gold text-charcoal font-black font-mono text-[9px] uppercase tracking-widest py-3 rounded-full border-2 border-charcoal shadow-[2px_2px_0px_#1A1A1A] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none disabled:opacity-60">
              {packing ? <Loader2 size={13} className="animate-spin" /> : <FileDown size={13} />} Apply Pack · 30 Cr
            </button>
          </div>
        </div>
      )}
      <CreditPaywall detail={paywall} onClose={() => setPaywall(null)} />
        </>
      )}
    </div>
  );
}
